/**
 * A value as stored, as JSON (part 24a, table R, deep-fix r2): a refused record goes into the manifest as it is, so its
 * words get out even though no reader takes it. IndexedDB stores any structured-cloneable value, and JSON holds less, so
 * text, finite numbers, true and false, null, lists and plain objects are kept and anything else (a date, a binary, a
 * map, a value met again on its own path, a level past {@link MAX_JSON_DEPTH}) is null.
 */

/** A JSON value. */
export type JsonValue = string | number | boolean | null | readonly JsonValue[] | { readonly [key: string]: JsonValue };

/** How deep a value is followed: deeper than any record, short of the stack. */
export const MAX_JSON_DEPTH = 64;

/** J1–J5: `value` as JSON, by the rule above. */
export function jsonOf(value: unknown, path: readonly object[] = []): JsonValue {
  if (typeof value === 'string' || typeof value === 'boolean' || value === null) return value;
  // J2: -0 is 0 in JSON; NaN and the infinities have no JSON.
  if (typeof value === 'number') return Number.isFinite(value) ? value + 0 : null;
  if (typeof value !== 'object' || path.includes(value) || path.length >= MAX_JSON_DEPTH) return null;
  const inner = [...path, value];
  if (Array.isArray(value)) return value.map((item: unknown) => jsonOf(item, inner));
  const prototype: unknown = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) return null;
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, jsonOf(item, inner)]));
}

/**
 * Part 24b, MB6: whether `value` is a JSON value as {@link jsonOf} gives one: text, a finite number, true, false, null, or
 * a list or a plain object of such, at most {@link MAX_JSON_DEPTH} levels of lists and objects deep (so a value met again
 * on its own path, which a structured clone keeps, never is one).
 */
export function isJson(value: unknown, depth = 0): value is JsonValue {
  if (typeof value === 'string' || typeof value === 'boolean' || value === null) return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value !== 'object' || depth >= MAX_JSON_DEPTH) return false;
  if (Array.isArray(value)) return value.every((item: unknown) => isJson(item, depth + 1));
  const prototype: unknown = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) return false;
  return Reflect.ownKeys(value).every((key) => {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    return typeof key === 'string' && descriptor !== undefined && 'value' in descriptor && descriptor.enumerable === true && isJson(descriptor.value, depth + 1);
  });
}
